// 日期工具
//
// 显示用格式（formatDateLong / formatDateShort）走标准 Intl.DateTimeFormat，
// 并按 <html lang> 当前值自动切换语言 —— LocaleProvider 在 setLocale 时已同步设置。
// 这样这些纯函数就能在任何组件里调，无需 hook。
//
// 比较类（isToday / isOverdue / next7Days）是纯逻辑，不需要 i18n。

import {
  parseISO,
  format,
  isToday as _isToday,
  isPast,
  startOfDay,
  addDays,
  isWithinInterval,
} from 'date-fns';

function currentLang(): string {
  if (typeof document === 'undefined') return 'en';
  return document.documentElement.lang || 'en';
}

// 安全解析：非法日期串（损坏的同步行 / 手改的导入备份）解析出 Invalid Date 后，
// 喂给 Intl.format / date-fns format 会抛 RangeError —— 在渲染路径里这会让 React 子树崩，
// 在 WKWebView/桌面壳里就是「界面渲染了却点不动」的致命模式。统一在这里挡住。
export function safeParseISO(s: string | null | undefined): Date | null {
  if (!s) return null;
  const d = parseISO(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

export const todayStr = () => format(new Date(), 'yyyy-MM-dd');
export const tomorrowStr = () => format(addDays(new Date(), 1), 'yyyy-MM-dd');
export const formatDate = (d: string) => {
  const date = safeParseISO(d);
  return date ? format(date, 'yyyy-MM-dd') : '';
};

export const formatDateLong = (d: Date = new Date()) =>
  new Intl.DateTimeFormat(currentLang(), {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    weekday: 'long',
  }).format(d);

export const formatDateShort = (d: string) => {
  const date = safeParseISO(d);
  if (!date) return '';
  return new Intl.DateTimeFormat(currentLang(), {
    month: 'short',
    day: 'numeric',
  }).format(date);
};

// 「时点任务」(dueAt) 的时分，按当前 locale 展示（如 14:30 / 2:30 PM）。
export const formatTimeShort = (iso: string) => {
  const date = safeParseISO(iso);
  if (!date) return '';
  return new Intl.DateTimeFormat(currentLang(), {
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

export const isToday = (d?: string | null) => {
  const date = safeParseISO(d);
  return !!date && _isToday(date);
};
export const isOverdue = (d?: string | null) => {
  const date = safeParseISO(d);
  if (!date) return false;
  return isPast(date) && !_isToday(date);
};

export const inRangeToday = (start?: string | null, end?: string | null) => {
  const s = safeParseISO(start);
  const e = safeParseISO(end);
  // 损坏 / 反转区间（start>end）直接判否，别喂给 isWithinInterval（避免依赖其对非法区间的容忍度）。
  if (!s || !e || s > e) return false;
  const today = startOfDay(new Date());
  return isWithinInterval(today, { start: s, end: e });
};

export const next7Days = () => {
  const days: string[] = [];
  for (let i = 0; i < 7; i++) {
    days.push(format(addDays(new Date(), i), 'yyyy-MM-dd'));
  }
  return days;
};
